class MobileMenu {
  constructor() {
    this.menuToggle = document.querySelector('[data-mobile-menu-toggle]');
    this.mobileMenu = document.getElementById('mobile-menu');
    this.menuItems = document.querySelectorAll('.header__menu-item--parent');
    
    if (!this.menuToggle || !this.mobileMenu) return;
    
    this.bindEvents();
    this.initDesktopSubmenuPositioning();
  }

  initDesktopSubmenuPositioning() {
    // Position third-level submenus aligned to top of second-level submenu
    const secondLevelItems = document.querySelectorAll('.header__submenu .header__menu-item--parent');
    
    secondLevelItems.forEach(item => {
      const thirdLevelSubmenu = item.querySelector('.header__submenu');
      if (!thirdLevelSubmenu) return;

      item.addEventListener('mouseenter', () => {
        if (window.innerWidth <= 1024) return; // Only for desktop
        
        this.alignThirdLevelSubmenu(item, thirdLevelSubmenu);
      });
    });
  }

  alignThirdLevelSubmenu(parentItem, thirdLevelSubmenu) {
    // Get the second level submenu (grandparent of third level)
    const secondLevelSubmenu = parentItem.closest('.header__submenu');
    if (!secondLevelSubmenu) return;

    // Get the position of current parent item within second level submenu
    const parentRect = parentItem.getBoundingClientRect();
    const submenuRect = secondLevelSubmenu.getBoundingClientRect();
    
    // Calculate offset to align third level to top of second level
    const offsetTop = submenuRect.top - parentRect.top;
    
    // Apply the positioning
    thirdLevelSubmenu.style.top = `${offsetTop}px`;
  }

  resetThirdLevelPositioning() {
    // Reset all third-level submenu positions when menu closes or on resize
    const thirdLevelSubmenus = document.querySelectorAll('.header__submenu .header__submenu');
    thirdLevelSubmenus.forEach(submenu => {
      submenu.style.top = '';
    });
  }

  bindEvents() {
    // Main menu toggle
    this.menuToggle.addEventListener('click', this.toggleMenu.bind(this));
    this.menuToggle.addEventListener('keydown', this.handleKeydown.bind(this));
    
    // Submenu toggles - only for mobile
    this.menuItems.forEach(item => {
      const link = item.querySelector('.header__menu-item-link');
      if (item.querySelector('.header__submenu')) {
        link.addEventListener('click', this.handleSubmenuToggle.bind(this));
        link.addEventListener('keydown', this.handleSubmenuKeydown.bind(this));
      }
    });

    // Close menu when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    
    // Close menu on escape
    document.addEventListener('keydown', this.handleEscapeKey.bind(this));
    
    // Handle window resize
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  toggleMenu() {
    const isOpen = this.menuToggle.getAttribute('aria-expanded') === 'true';
    
    this.menuToggle.setAttribute('aria-expanded', !isOpen);
    this.mobileMenu.setAttribute('aria-expanded', !isOpen);
    
    // Manage focus
    if (!isOpen) {
      // Menu opening
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        const firstLink = this.mobileMenu.querySelector('a');
        if (firstLink) firstLink.focus();
      }, 300);
    } else {
      // Menu closing
      document.body.style.overflow = '';
      this.menuToggle.focus();
      this.closeAllSubmenus();
    }
  }

  handleSubmenuToggle(event) {
    if (window.innerWidth > 1024) return; // Desktop uses hover
    
    const parentItem = event.currentTarget.closest('.header__menu-item--parent');
    if (!parentItem) return;
    
    event.preventDefault();
    
    const isOpen = parentItem.getAttribute('aria-expanded') === 'true';
    
    // Close sibling submenus at the same level
    this.closeSiblingSubmenus(parentItem);
    
    // Toggle current submenu
    parentItem.setAttribute('aria-expanded', !isOpen);
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleMenu();
    }
  }

  handleSubmenuKeydown(event) {
    if (window.innerWidth > 1024) return;
    
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleSubmenuToggle(event);
    }
  }

  handleOutsideClick(event) {
    if (window.innerWidth > 1024) return;
    
    const isMenuOpen = this.menuToggle.getAttribute('aria-expanded') === 'true';
    const isClickInsideMenu = this.mobileMenu.contains(event.target);
    const isClickOnToggle = this.menuToggle.contains(event.target);
    
    if (isMenuOpen && !isClickInsideMenu && !isClickOnToggle) {
      this.toggleMenu();
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      const isMenuOpen = this.menuToggle.getAttribute('aria-expanded') === 'true';
      if (isMenuOpen) {
        this.toggleMenu();
      }
    }
  }

  handleResize() {
    if (window.innerWidth > 1024) {
      // Desktop view - reset mobile menu
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.mobileMenu.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      this.closeAllSubmenus();
    } else {
      // Mobile view - reset third level positioning
      this.resetThirdLevelPositioning();
    }
  }

  closeAllSubmenus() {
    // Close all submenus at all levels
    const allParentItems = document.querySelectorAll('.header__menu-item--parent');
    allParentItems.forEach(item => {
      item.setAttribute('aria-expanded', 'false');
    });
    this.resetThirdLevelPositioning();
  }

  closeSiblingSubmenus(currentItem) {
    // Helper method to close only sibling submenus at the same level
    const parentContainer = currentItem.closest('.header__submenu') || currentItem.closest('.header__menu');
    
    if (parentContainer) {
      const siblingParentItems = parentContainer.children;
      for (let i = 0; i < siblingParentItems.length; i++) {
        const sibling = siblingParentItems[i];
        if (sibling !== currentItem && 
            sibling.classList.contains('header__menu-item--parent')) {
          sibling.setAttribute('aria-expanded', 'false');
          // Also close all nested submenus within this sibling
          const nestedSubmenus = sibling.querySelectorAll('.header__menu-item--parent');
          nestedSubmenus.forEach(nested => {
            nested.setAttribute('aria-expanded', 'false');
          });
        }
      }
    }
  }

  destroy() {
    // Clean up event listeners
    if (this.menuToggle) {
      this.menuToggle.removeEventListener('click', this.toggleMenu.bind(this));
      this.menuToggle.removeEventListener('keydown', this.handleKeydown.bind(this));
    }

    this.menuItems.forEach(item => {
      const link = item.querySelector('.header__menu-item-link');
      if (link && item.querySelector('.header__submenu')) {
        link.removeEventListener('click', this.handleSubmenuToggle.bind(this));
        link.removeEventListener('keydown', this.handleSubmenuKeydown.bind(this));
      }
    });

    document.removeEventListener('click', this.handleOutsideClick.bind(this));
    document.removeEventListener('keydown', this.handleEscapeKey.bind(this));
    window.removeEventListener('resize', this.handleResize.bind(this));
    
    // Reset styles
    document.body.style.overflow = '';
    this.closeAllSubmenus();
  }
}

// Initialize Mobile Menu
let mobileMenuInstance;

function initializeMobileMenu() {
  // Clean up existing instance
  if (mobileMenuInstance && mobileMenuInstance.destroy) {
    mobileMenuInstance.destroy();
  }
  
  // Initialize new instance
  mobileMenuInstance = new MobileMenu();
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMobileMenu);
} else {
  initializeMobileMenu();
}

// Re-initialize on section reload (Theme Editor)
document.addEventListener('shopify:section:load', initializeMobileMenu);

// Export for manual initialization if needed
if (typeof window !== 'undefined') {
  window.MobileMenu = MobileMenu;
  window.initializeMobileMenu = initializeMobileMenu;
}
